import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, isUndefined } from 'lodash';

import { SidebarContext } from '../PageLayout/ContextProvider';
import { filterReportsByFlight } from '../../actions/reports';
import { redirectPage } from '../../actions/screen';
import { setInitialOfp } from '../../actions/systemOfMeasurement';
import { setCurrentTask } from '../../actions/tasks';
import { getCurrentLegCrewType, getCurrentTaskNumberSelector, getCurrentFlight } from '../../selectors/screen';
import { reconcilePartitions } from '../../reducers/partitions';
import { getButtonType, sidebarItemClick } from './tools';

export const useSidebarContent = () => {
    const { handleToggleNavigationPanel } = useContext(SidebarContext);

    const currentTask = useSelector(getCurrentTaskNumberSelector);
    const currentLeg = useSelector((state) => get(state, 'screen.currentLeg'));
    const currentFlight = useSelector(getCurrentFlight);
    const currentFlightId = currentFlight && currentFlight.id;
    const canDownloadTaskData = !isUndefined(currentTask);
    const crewType = useSelector(getCurrentLegCrewType);
    const { page, showNoticePreview, currentContentType, partitions } = useSelector((state) => {
        const pathname = get(state, 'router.location.pathname');
        const isNoticeAllowed = get(state.routesDeclaration, 'options.allowNotices', false);
        const isNoticePreviewAllowed = get(state.routesDeclaration, 'options.allowNoticesPreview', false);
        const partitions = reconcilePartitions(state.partitions);
        const currentContentType = state.screen?.attachmentContentType;

        return {
            page: pathname.substring(1).toLowerCase(),
            showNoticePreview: !isNoticeAllowed && isNoticePreviewAllowed,
            currentContentType,
            partitions,
        };
    });

    const [value, setValue] = useState();

    const buttonType = useMemo(() => getButtonType(page, currentContentType), [page, currentContentType]);

    const needSetValue = value === undefined || buttonType !== value;

    useEffect(() => {
        if (needSetValue) {
            setValue(buttonType);
        }
    }, [needSetValue, buttonType]);

    const dispatch = useDispatch();

    const handleSetValue = useCallback((value) => setValue(value), [setValue]);

    const handleRedirectPage = useCallback((path) => {
        if (path === 'flight-task') {
            dispatch(setCurrentTask());
            dispatch(setInitialOfp());
        }

        if (path === 'reports') {
            dispatch(filterReportsByFlight());
        }

        dispatch(redirectPage(path));
    }, []);

    const handleItemClick = useCallback(
        (value) => {
            handleToggleNavigationPanel(false);
            sidebarItemClick(
                value,
                currentLeg,
                currentTask,
                currentFlightId,
                dispatch,
                handleRedirectPage,
                handleSetValue,
                showNoticePreview
            );
        },
        [currentLeg, currentTask, currentFlightId, dispatch, handleRedirectPage, handleSetValue, showNoticePreview]
    );

    return {
        value,
        canDownloadTaskData,
        crewType,
        partitions,
        handleItemClick,
    };
};
